<script setup lang="ts">
    import InfoIcon from "@/assets/info_dark.svg";
    import KFormFieldDisplay from "@/components/forms/displays/FormFieldDisplay/KFormFieldDisplay.vue";
    import KForm from "@/components/forms/Form/KForm.vue";
    import ModalContent from "@/components/generic/ModalContent.vue";
    import { useStore } from "@/store";
    import {
        getLockName,
        NllLock,
        NllLockSubject,
        NllLockType,
        NllLockTypeDescription,
        SparrValue,
    } from "@/store/modules/recept/helpers";
    import { checkNllLocks } from "@/tabs/Receptforskrivning/helpers";
    import { ReceptforskrivningDisplay } from "@/tabs/Receptforskrivning/types";
    import { getAge } from "@/utils/patient/patientData";
    import { defineProps, onMounted, ref, toRefs } from "vue";

    const store = useStore();

    const props = defineProps<{
        receptforskrivning: ReceptforskrivningDisplay | null;
        overrideOptions?: Array<typeof NllLock[keyof typeof NllLock]>;
        overrideValue?: SparrValue;
        clearable?: boolean;
    }>();

    const { receptforskrivning, overrideOptions, overrideValue } = toRefs(props);

    const sparrAutocomplete = ref();
    const options = ref<Array<typeof NllLock[keyof typeof NllLock]>>([]);

    const lockTypeDescriptions = [NllLockTypeDescription.sekretess, NllLockTypeDescription.integritet];

    const formListeners: Array<{
        fieldNames: string[];
        action: ({ values }: { values: { [key: string]: unknown } }) => void;
    }> = [
        {
            fieldNames: ["sparr"],
            action: ({ values }) => {
                selectedSparr.value = values.sparr as SparrValue;
            },
        },
    ];

    const emit = defineEmits<{
        (event: "submit", payload: SparrValue): void;
        (event: "cancel"): void;
    }>();

    const selectedSparr = ref<SparrValue | null>(overrideValue?.value ? overrideValue.value : null);

    const onSubmit = (data: unknown) => {
        emit("submit", (data as { sparr: SparrValue }).sparr);
    };

    const onCancel = () => {
        emit("cancel");
    };

    const setOptions = () => {
        const optionsToAdd: Array<typeof NllLock[keyof typeof NllLock]> = [];

        if (overrideOptions?.value) {
            options.value = overrideOptions.value;
            sparrAutocomplete?.value?.setOptions?.({ options: overrideOptions.value, setDefault: true });

            return;
        }

        const locks = checkNllLocks({ sparrar: receptforskrivning.value?.sparrar });

        if (!locks?.behandlingsorsak) {
            if (!receptforskrivning.value?.uttag_pappersrecept) {
                optionsToAdd.push(NllLock[`${NllLockType.SEKRETESS_PAT.code}-${NllLockSubject.BEHANDLINGSORSAK.code}`]);
            }
        }

        if (!locks?.sekretessVardnadshavare) {
            if (receptforskrivning.value?.uttag_pappersrecept) {
                optionsToAdd.push(
                    NllLock[`${NllLockType.SEKRETESS_VH.code}-${NllLockSubject.UTTAGPAPPERSRECEPT.code}`]
                );
            } else {
                optionsToAdd.push(
                    NllLock[`${NllLockType.SEKRETESS_VH.code}-${NllLockSubject.FORSKRIVNINGSKEDJA.code}`]
                );
            }
        }

        const filteredOptions = optionsToAdd.filter((option) => {
            if (option.maxAge) {
                const age = getAge({ dateOfBirth: store.getters["patientData/dateOfBirth"] });

                if (age) {
                    return age <= option.maxAge;
                }

                return true;
            }

            return true;
        });

        options.value = filteredOptions;
        sparrAutocomplete?.value?.setOptions?.({ options: filteredOptions, setDefault: true });
    };

    onMounted(() => {
        setOptions();
    });
</script>

<template>
    <ModalContent title="Skapa spärr">
        <KForm
            id="sparr"
            submitContent="Skapa spärr"
            cancelContent="Avbryt"
            submitVariant="alert"
            :listeners="formListeners"
            class="sparr-forskrivning"
            @submit="onSubmit"
            @cancel="onCancel"
        >
            <div class="form-content">
                <div :class="['form-section', 'span-all', { divider: !selectedSparr }]">
                    <KAutocomplete
                        :value="overrideValue"
                        ref="sparrAutocomplete"
                        name="sparr"
                        :options="[]"
                        class="span-all"
                        label="Typ av spärr"
                        validation="required"
                        :clearable="clearable"
                    />
                    <KGroup
                        v-if="!selectedSparr"
                        name="sparr-descriptions"
                        label="Information om spärrar"
                        class="span-all"
                        size="large"
                    >
                        <KFormFieldDisplay
                            v-for="option in options"
                            :key="getLockName({ value: option.value })"
                            :name="`description-${getLockName({ value: option.value })}`"
                            :label="option.label"
                            :value="option.description"
                            class="span-all"
                            labelVariant="dark"
                            labelWeight="bold"
                        />
                    </KGroup>
                </div>
                <div v-if="!selectedSparr" class="form-section span-all">
                    <KFormFieldDisplay
                        v-for="(description, index) in lockTypeDescriptions"
                        :key="index"
                        :name="`sparr-typ-${index}`"
                        class="span-all"
                        labelVariant="dark"
                        labelWeight="bold"
                        :label="description.label"
                        :value="description.description"
                    />
                </div>
                <div v-else class="form-section span-all">
                    <KGroup
                        name="disclaimer-group"
                        class="span-all disclaimer"
                        rowGap="half"
                        :freeFlow="true"
                        :label="NllLock[getLockName({ value: selectedSparr })]?.label"
                    >
                        <img :src="InfoIcon" alt="Disclaimer" class="icon" />
                        <KFormFieldDisplay
                            name="disclaimer"
                            class="text"
                            labelVariant="dark"
                            labelWeight="bold"
                            :value="NllLock[getLockName({ value: selectedSparr })]?.disclaimer"
                        />
                    </KGroup>
                    <KFormFieldDisplay
                        name="infoNll"
                        label="Informationstext från NLL:"
                        labelVariant="dark"
                        labelWeight="bold"
                        class="span-all"
                        :value="NllLock[getLockName({ value: selectedSparr })]?.infoNll"
                    />
                </div>
            </div>
        </KForm>
    </ModalContent>
</template>

<style lang="scss" scoped>
    @import "@/style/variables";

    .sparr-forskrivning {
        .disclaimer {
            ::v-deep .group-content {
                flex-wrap: unset;
            }

            .icon {
                width: 16px;
                height: $line-height-default;
                align-self: flex-start;
            }
        }
    }
</style>
